import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';

import COLORS from '../constants/colors';

export default function VerificationCodeInput({
  length,
  value,
  onChange,
  disabled,
  idPrefix,
  label,
  autoFocus,
}) {
  const inputsRef = useRef([]);
  const hiddenInputRef = useRef(null);
  const isPastingRef = useRef(false);
  const sanitizedValue = useMemo(() => {
    if (!value) {
      return '';
    }
    return String(value)
      .replace(/[^0-9]/g, '')
      .slice(0, length);
  }, [length, value]);

  const digits = useMemo(() => {
    const result = [];
    for (let index = 0; index < length; index += 1) {
      result.push(sanitizedValue[index] || '');
    }
    return result;
  }, [length, sanitizedValue]);

  const focusInput = (index) => {
    const element = inputsRef.current[index];
    if (element) {
      element.focus();
      element.select();
    }
  };

  const emitValue = (nextValue) => {
    if (onChange) {
      onChange(nextValue);
    }
  };

  const handleChange = (event, index) => {
    // If we're handling a paste, skip this onChange handler
    // The paste handler will take care of it
    if (isPastingRef.current) {
      return;
    }

    const raw = event.target.value;
    const numeric = raw.replace(/[^0-9]/g, '');
    if (!numeric) {
      const current = digits.join('');
      const updated =
        current.slice(0, index) + current.slice(index + 1, length);
      emitValue(updated);
      return;
    }

    // If multiple characters are entered (e.g., from paste that wasn't caught), handle as paste
    if (numeric.length > 1) {
      // Start filling from the current index
      const currentDigits = digits.slice(0);
      let fillIndex = index;
      for (let i = 0; i < numeric.length && fillIndex < length; i++) {
        currentDigits[fillIndex] = numeric[i];
        fillIndex++;
      }
      const nextValue = currentDigits.join('').slice(0, length);
      emitValue(nextValue);
      // Focus the last filled input or the last input if all are filled
      const focusIndex = Math.min(fillIndex - 1, length - 1);
      focusInput(focusIndex);
      return;
    }

    // Single character input (normal typing)
    const currentDigits = digits.slice(0);
    const nextChar = numeric.slice(-1);
    currentDigits[index] = nextChar;
    const nextValue = currentDigits.join('').slice(0, length);
    emitValue(nextValue);

    if (index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Backspace') {
      if (digits[index]) {
        const current = digits.join('');
        const updated =
          current.slice(0, index) + current.slice(index + 1, length);
        emitValue(updated);
      } else if (index > 0) {
        focusInput(index - 1);
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
      event.preventDefault();
    } else if (event.key === 'ArrowRight' && index < length - 1) {
      focusInput(index + 1);
      event.preventDefault();
    }
  };

  const handlePaste = (event, startIndex = 0) => {
    event.preventDefault();
    event.stopPropagation();

    // Set flag to prevent onChange from interfering
    isPastingRef.current = true;

    const text = event.clipboardData.getData('text');
    if (!text) {
      isPastingRef.current = false;
      return;
    }
    const numeric = text.replace(/[^0-9]/g, '').slice(0, length - startIndex);
    if (!numeric) {
      isPastingRef.current = false;
      return;
    }

    // Start filling from the specified index
    const currentDigits = digits.slice(0);
    let fillIndex = startIndex;
    for (let i = 0; i < numeric.length && fillIndex < length; i++) {
      currentDigits[fillIndex] = numeric[i];
      fillIndex++;
    }
    const nextValue = currentDigits.join('').slice(0, length);
    emitValue(nextValue);

    // Reset flag after a short delay to allow state to update
    setTimeout(() => {
      isPastingRef.current = false;
      // Focus the last filled input or the last input if all are filled
      const focusIndex = Math.min(fillIndex - 1, length - 1);
      focusInput(focusIndex);
    }, 0);
  };

  const handleHiddenInputChange = (event) => {
    const raw = event.target.value;
    const numeric = raw.replace(/[^0-9]/g, '').slice(0, length);
    if (numeric) {
      emitValue(numeric);
      // Clear the hidden input after processing
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = '';
      }
      // Focus the appropriate input based on how many digits were filled
      const focusIndex = Math.min(numeric.length - 1, length - 1);
      focusInput(focusIndex);
    }
  };

  const prefix = idPrefix || 'code';
  const labelText = label || '';
  const groupLabel = labelText || 'Verification code input';

  useEffect(() => {
    if (autoFocus && !disabled) {
      focusInput(0);
    }
  }, [autoFocus, disabled]);

  return (
    <div id={`${prefix}_container`} style={{ marginBottom: 5 }}>
      {labelText && (
        <div
          style={{
            color: COLORS.MAIN_GRAY,
            alignSelf: 'center',
            fontSize: 14,
          }}>
          {labelText.toUpperCase()}
        </div>
      )}
      {/* Hidden input for iOS SMS autofill */}
      <input
        ref={hiddenInputRef}
        type='text'
        inputMode='numeric'
        autoComplete='one-time-code'
        onChange={handleHiddenInputChange}
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none',
        }}
        aria-hidden='true'
        tabIndex={-1}
      />
      <div
        className='auth-code-input'
        onPaste={handlePaste}
        role='group'
        aria-label={groupLabel}>
        {digits.map((digit, index) => (
          <input
            key={`${prefix}-${index}`}
            id={`${prefix}-${index}`}
            type='text'
            inputMode='numeric'
            autoComplete='off'
            value={digit}
            ref={(element) => {
              inputsRef.current[index] = element;
            }}
            onChange={(event) => handleChange(event, index)}
            onPaste={(event) => handlePaste(event, index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            maxLength={length}
            disabled={disabled}
            className='auth-code-input__digit'
          />
        ))}
      </div>
      <div style={{ clear: 'both' }}></div>
    </div>
  );
}

VerificationCodeInput.defaultProps = {
  length: 6,
  value: '',
  disabled: false,
  idPrefix: 'verification-code',
  label: '',
  autoFocus: false,
};

VerificationCodeInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  idPrefix: PropTypes.string,
  label: PropTypes.string,
  autoFocus: PropTypes.bool,
};
